<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import axios from 'axios';
  import { useUi } from '@/composition/use-ui';
  import { useModal } from '@/composition/use-modal';
  import {
    getEffectiveTier,
    getPurchasedTier,
  } from '@/utils/billing-entitlements.utils';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import BillingService, {
    IBillingPlanCatalogItem,
    IBillingPlansResponse,
    IBillingSubscriptionSummaryResponse,
  } from '@/api/services/billing.service';
  import RestaurantsService from '@/api/services/restaurants.service';
  import { AlertType } from '@/interfaces/IAlert';
  import BillingPlanPicker from './BillingPlanPicker.vue';
  import BillingSubscriptionManageCard from './BillingSubscriptionManageCard.vue';
  import BillingLegacyPlanStatusCard from './BillingLegacyPlanStatusCard.vue';
  import { vueData } from '@/VueData';
  import type { IRestaurant } from '@/interfaces/IRestaurant';
  import type { ICurrency } from '@/interfaces/ICurrency';
  import type { IUser } from '@/interfaces/IUser';

  const props = defineProps<{
    restaurant: IRestaurant;
    currency: ICurrency;
    user: IUser;
  }>();

  const ui = useUi();
  const modals = useModal();
  const restaurantStore = useRestaurantStore();

  const billingService = new BillingService();

  const catalog = ref<IBillingPlansResponse | null>(null);
  const subscription = ref<IBillingSubscriptionSummaryResponse | null>(null);
  const loading = ref(false);
  const purchasingTier = ref<number | null>(null);
  const selectedPeriod = ref<number>(12);
  const billingUnavailable = ref(false);
  const loadRequestId = ref(0);
  const uiMode = ref<'manage' | 'choose'>('choose');
  const choosePurpose = ref<'purchase' | 'change'>('purchase');

  const nowMs = () => Date.now();

  function getRenewalInProgressMessage(e: unknown): string | null {
    if (!axios.isAxiosError(e)) return null;
    const data = e.response?.data;
    if (data?.code !== 'BILLING_RENEWAL_IN_PROGRESS') return null;
    return typeof data.message === 'string' && data.message
      ? data.message
      : 'Su renovación está en proceso. Su plan sigue activo y no hace falta comprar uno nuevo.';
  }

  function tierLabelFor(tier: number) {
    if (!tier) return 'Free';
    if (tier === 1) return 'Plus';
    return 'Pro';
  }

  const planExpiresAt = computed(() => {
    if (!props.restaurant?.planExpiresAt) return null;
    const d = new Date(props.restaurant.planExpiresAt);
    return Number.isNaN(d.getTime()) ? null : d;
  });

  const planExpiresAtIso = computed(() => {
    return planExpiresAt.value ? planExpiresAt.value.toISOString() : null;
  });

  const isPlanExpired = computed(() => {
    if (!planExpiresAt.value) return false;
    return planExpiresAt.value.getTime() <= nowMs();
  });

  const effectiveTier = computed(() =>
    getEffectiveTier(props.restaurant, nowMs())
  );

  const purchasedTier = computed(() => getPurchasedTier(props.restaurant));

  const purchasedTierLabel = computed(() => {
    return tierLabelFor(purchasedTier.value);
  });

  const currentTierLabel = computed(() => {
    const tier = effectiveTier.value;
    if (!tier) return 'Free';
    if (tier === 1) return 'Plus';
    return 'Pro';
  });

  const subscriptionTierLabel = computed(() => {
    const tier = subscription.value?.subscriptionTier;
    if (typeof tier !== 'number') return currentTierLabel.value;
    return tierLabelFor(tier);
  });

  const hasPaidAccess = computed(() => {
    return effectiveTier.value > 0;
  });

  const recoveryAction = computed(() => {
    return subscription.value?.recoveryAction ?? 'none';
  });

  const renewalPending = computed(() => {
    return subscription.value?.renewalPending === true;
  });

  const hasManageableSubscription = computed(() => {
    const status = subscription.value?.status;
    return status === 'ACTIVE' || status === 'CANCELLED';
  });

  const isLegacyActiveWithoutSubscription = computed(() => {
    if (!subscription.value) return false;
    if (subscription.value.status !== 'NONE') return false;
    return hasPaidAccess.value;
  });

  const isStripeProvider = computed(() => {
    return subscription.value?.provider === 'stripe';
  });

  const showPausedStripeSupport = computed(() => {
    return isStripeProvider.value && subscription.value?.status === 'PAUSED';
  });

  const showManageStripeCustomerPortal = computed(() => {
    if (!subscription.value) return false;
    if (!isStripeProvider.value) return false;
    if (subscription.value.status === 'ACTIVE') return true;
    return false;
  });

  const showRecoveryUpdatePaymentMethod = computed(() => {
    return recoveryAction.value === 'update_payment_method';
  });

  const showRecoveryResubscribe = computed(() => {
    return recoveryAction.value === 'resubscribe';
  });

  type TBillingPlansScreenMode =
    | 'default'
    | 'paused_stripe_support'
    | 'update_payment_method'
    | 'resubscribe'
    | 'unavailable';

  const screenMode = computed<TBillingPlansScreenMode>(() => {
    if (billingUnavailable.value) return 'unavailable';
    if (showRecoveryUpdatePaymentMethod.value) return 'update_payment_method';
    if (showPausedStripeSupport.value) return 'paused_stripe_support';
    if (showRecoveryResubscribe.value) return 'resubscribe';
    return 'default';
  });

  const canChangeViaProvider = computed(() => {
    if (!subscription.value) return false;
    if (subscription.value.status !== 'ACTIVE') return false;
    return !subscription.value.cancelAtPeriodEnd;
  });

  const canResumeStripeCancellation = computed(() => {
    if (!subscription.value) return false;
    if (!isStripeProvider.value) return false;
    if (subscription.value.status !== 'ACTIVE') return false;
    return subscription.value.cancelAtPeriodEnd === true;
  });

  const manageStatus = computed(() => {
    if (renewalPending.value) {
      return { text: 'Renovando', color: 'info' as const };
    }

    if (isPlanExpired.value) {
      return { text: 'Vencido', color: 'warning' as const };
    }

    if (subscription.value?.pendingConfirmation) {
      return { text: 'Confirmando', color: 'info' as const };
    }

    if (
      subscription.value?.cancelAtPeriodEnd ||
      subscription.value?.status === 'CANCELLED'
    ) {
      return { text: 'Cancelada', color: 'info' as const };
    }

    if (hasPaidAccess.value) {
      return { text: 'Activa', color: 'success' as const };
    }

    return { text: 'Sin plan', color: 'grey' as const };
  });

  const legacyStatus = computed(() => {
    if (isPlanExpired.value) {
      return { text: 'Vencido', color: 'warning' as const };
    }

    if (purchasedTier.value > 0) {
      return { text: 'Activa', color: 'success' as const };
    }

    return { text: 'Sin plan', color: 'grey' as const };
  });

  const canCancel = computed(() => {
    if (!subscription.value) return false;
    if (subscription.value.status !== 'ACTIVE') return false;
    return !subscription.value.cancelAtPeriodEnd;
  });

  const managePrimaryActionText = computed(() => {
    if (
      subscription.value?.cancelAtPeriodEnd ||
      subscription.value?.status === 'CANCELLED'
    ) {
      return 'Resuscribirse';
    }
    return 'Cambiar plan';
  });

  const currentPeriodMonths = computed(
    () => subscription.value?.periodMonths ?? null
  );

  const showManage = computed(() => {
    if (billingUnavailable.value) return false;
    if (!hasPaidAccess.value) return false;
    if (!hasManageableSubscription.value) return false;
    return uiMode.value === 'manage';
  });

  const showPlanPicker = computed(() => {
    if (screenMode.value === 'unavailable') return false;
    if (screenMode.value === 'paused_stripe_support') return false;
    if (screenMode.value === 'update_payment_method') return false;
    return true;
  });

  const showLegacyPlanStatusCard = computed(() => {
    if (purchasedTier.value <= 0) return false;
    if (!subscription.value) return false;
    return subscription.value.status === 'NONE';
  });

  const chooseTitle = computed(() => {
    if (screenMode.value === 'update_payment_method')
      return 'Actualizar medio de pago';
    if (screenMode.value === 'paused_stripe_support')
      return 'Suscripción pausada';
    if (screenMode.value === 'resubscribe') return 'Reactivar suscripción';
    if (choosePurpose.value === 'change') return 'Cambiar plan';
    if (isPlanExpired.value) return 'Renovar plan';
    return 'Elija su plan';
  });

  const chooseSubtitle = computed(() => {
    if (screenMode.value === 'update_payment_method') {
      return 'Actualice su medio de pago para recuperar la suscripción.';
    }
    if (screenMode.value === 'paused_stripe_support') {
      return 'Contacte a soporte para reactivarla.';
    }
    if (screenMode.value === 'resubscribe') {
      return 'Elija un plan para activar una nueva suscripción.';
    }
    if (choosePurpose.value === 'change') {
      return 'Importante: el cambio puede generar un prorrateo que se reflejará en el próximo cobro.';
    }
    return 'Pagos seguros. Cancele cuando quiera.';
  });

  const recoveryAlertText = computed(() => {
    if (showRecoveryUpdatePaymentMethod.value) {
      return 'Su suscripción tiene un problema de cobro. Actualice el medio de pago para recuperarla.';
    }

    if (showPausedStripeSupport.value) {
      return 'Su suscripción está pausada. Contacte a soporte para reactivarla.';
    }

    if (showRecoveryResubscribe.value) {
      return 'Su suscripción actual tuvo un problema de cobro. Active una nueva suscripción y la anterior se cancelará automáticamente cuando la nueva quede activa.';
    }

    return null;
  });

  async function loadBillingState() {
    if (!props.restaurant?.id) return;
    const requestId = ++loadRequestId.value;

    billingUnavailable.value = false;
    catalog.value = null;
    subscription.value = null;
    uiMode.value = 'choose';
    choosePurpose.value = 'purchase';

    try {
      loading.value = true;
      const [cat, sub] = await Promise.all([
        billingService.getPlans(props.restaurant.id),
        billingService.getSubscription(props.restaurant.id).catch(() => null),
      ]);

      if (requestId !== loadRequestId.value) return;

      catalog.value = cat;
      subscription.value = sub;

      if (catalog.value?.periods?.includes(12)) {
        selectedPeriod.value = 12;
      } else if (catalog.value?.periods?.includes(1)) {
        selectedPeriod.value = 1;
      }

      uiMode.value =
        hasPaidAccess.value && hasManageableSubscription.value
          ? 'manage'
          : 'choose';
      choosePurpose.value = 'purchase';
    } catch (e) {
      if (requestId !== loadRequestId.value) return;
      billingUnavailable.value = true;
    } finally {
      if (requestId !== loadRequestId.value) return;
      loading.value = false;
    }
  }

  watch(
    () => props.restaurant?.id,
    () => loadBillingState(),
    { immediate: true }
  );

  async function refreshRestaurant() {
    try {
      if (props.restaurant?.stringId) {
        const full = await RestaurantsService.getFullRestaurantBySlug(
          props.restaurant.stringId
        );
        vueData.setCurrentRestaurant(full);
        vueData.setCurrentRestaurantSettings(full.settings ?? null);
      }
    } catch (e) {
      // ignore; UI will still reflect subscription state once refreshed
    }
  }

  async function refreshSubscription() {
    if (!props.restaurant?.id) return;
    try {
      subscription.value = await billingService.getSubscription(
        props.restaurant.id
      );
    } catch (e) {
      subscription.value = null;
    }
  }

  async function reconcileSubscriptionSilent(input?: { maxWaitMs?: number }) {
    if (!props.restaurant?.id) return null;
    try {
      const res = await billingService.reconcileSubscription(
        props.restaurant.id,
        {
          maxWaitMs: input?.maxWaitMs,
        }
      );
      subscription.value = res.summary;
      if (
        !res.summary.pendingConfirmation ||
        res.reconcile.processedSnapshots > 0
      ) {
        await refreshRestaurant();
      }
      return res.summary;
    } catch (e) {
      return null;
    }
  }

  async function reconcileSubscription(input?: { maxWaitMs?: number }) {
    if (!props.restaurant?.id) return;
    try {
      loading.value = true;
      const res = await billingService.reconcileSubscription(
        props.restaurant.id,
        {
          maxWaitMs: input?.maxWaitMs,
        }
      );
      subscription.value = res.summary;
      if (
        !res.summary.pendingConfirmation ||
        res.reconcile.processedSnapshots > 0
      ) {
        await refreshRestaurant();
      }

      await restaurantStore.loadBillingSubscriptionSummary({ force: true });
    } catch (e) {
      ui.serverError(e, 'No se pudo actualizar el estado de la suscripción');
    } finally {
      loading.value = false;
    }
  }

  async function refreshPendingConfirmation() {
    await reconcileSubscription({ maxWaitMs: 2500 });
  }

  async function pollReconcileUntilConfirmed() {
    const timeoutMs = 60000;
    const started = Date.now();
    let delayMs = 800;

    // One best-effort longer attempt first (improves UX after plan-change).
    const first = await reconcileSubscriptionSilent({ maxWaitMs: 2500 });
    if (first && !first.pendingConfirmation) return true;

    while (Date.now() - started < timeoutMs) {
      const summary = await reconcileSubscriptionSilent({ maxWaitMs: 0 });
      if (summary && !summary.pendingConfirmation) return true;

      await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
      delayMs = Math.min(Math.round(delayMs * 1.5), 4000);
    }

    return false;
  }

  async function resumeStripeSubscription() {
    if (!props.restaurant?.id) return;

    try {
      loading.value = true;
      const summary = await billingService.resumeSubscription(
        props.restaurant.id
      );
      subscription.value = summary;
      ui.showToast(AlertType.Success, 'Suscripción reanudada');
      await Promise.all([refreshRestaurant(), refreshSubscription()]);
      await restaurantStore.loadBillingSubscriptionSummary({ force: true });
    } catch (e) {
      ui.serverError(e, 'No se pudo reanudar la suscripción');
    } finally {
      loading.value = false;
    }
  }

  async function handlePrimaryAction() {
    if (renewalPending.value) {
      ui.showToast(
        AlertType.Info,
        'Su renovación está en proceso. Podrá hacer cambios cuando se confirme el pago.'
      );
      return;
    }

    if (canResumeStripeCancellation.value) {
      await resumeStripeSubscription();
      return;
    }

    enterChangePlan();
  }

  function enterChangePlan() {
    if (renewalPending.value) {
      ui.showToast(
        AlertType.Info,
        'Su renovación está en proceso. Podrá hacer cambios cuando se confirme el pago.'
      );
      return;
    }

    choosePurpose.value = canChangeViaProvider.value ? 'change' : 'purchase';

    const current = subscription.value?.periodMonths ?? null;
    if (choosePurpose.value === 'change' && (current === 1 || current === 12)) {
      selectedPeriod.value = current;
    }

    uiMode.value = 'choose';
  }

  function exitChoose() {
    choosePurpose.value = 'purchase';
    uiMode.value =
      hasPaidAccess.value && hasManageableSubscription.value
        ? 'manage'
        : 'choose';
  }

  async function startCheckout(plan: IBillingPlanCatalogItem) {
    if (!props.restaurant?.id) return;
    if (plan.tier === 0) return;

    if (canResumeStripeCancellation.value) {
      ui.showToast(
        AlertType.Info,
        'Su suscripción está programada para cancelarse al final del período. Reanúdela para continuar.'
      );
      return;
    }

    try {
      purchasingTier.value = plan.tier;
      const res = await billingService.createCheckoutSession({
        restaurantId: props.restaurant.id,
        tier: plan.tier,
        subTier: plan.subTier ?? null,
        periodMonths: selectedPeriod.value,
        email: props.user?.email ?? undefined,
      });

      if (!res.redirectUrl) {
        ui.showToast(AlertType.Error, 'No se pudo iniciar el checkout');
        return;
      }

      // Redirect to provider hosted subscription checkout
      window.location.href = res.redirectUrl;
    } catch (e) {
      const renewalMessage = getRenewalInProgressMessage(e);
      if (renewalMessage) {
        ui.showToast(AlertType.Info, renewalMessage);
        // The on-demand reconcile applies a provider snapshot, restoring the
        // grace entitlements so the UI flips to the manage card immediately.
        await reconcileSubscription();
        exitChoose();
      } else {
        ui.serverError(e, 'No se pudo iniciar el checkout');
      }
    } finally {
      purchasingTier.value = null;
    }
  }

  async function cancelSubscription() {
    if (!props.restaurant?.id) return;

    const confirmed = await modals.confirm({
      title: 'Cancelar suscripción',
      message:
        'Se detendrán los cobros futuros. ' +
        (renewalPending.value
          ? 'Como su período ya terminó, si cancela ahora perderá el acceso de inmediato.'
          : subscription.value?.nextBillingAt
          ? `Mantendrá el acceso hasta ${new Date(
              subscription.value.nextBillingAt
            ).toLocaleDateString()}.`
          : 'Mantendrá el acceso hasta el fin del período actual.'),
      acceptText: 'Cancelar suscripción',
      cancelText: 'Volver',
      acceptColor: 'error',
    });

    if (!confirmed) return;

    try {
      loading.value = true;
      await billingService.cancelSubscription(props.restaurant.id);
      ui.showToast(AlertType.Success, 'Suscripción cancelada');
      await Promise.all([refreshRestaurant(), refreshSubscription()]);
      await restaurantStore.loadBillingSubscriptionSummary({ force: true });
    } catch (e) {
      ui.serverError(e, 'No se pudo cancelar la suscripción');
    } finally {
      loading.value = false;
    }
  }

  async function changePlan(plan: IBillingPlanCatalogItem) {
    if (!props.restaurant?.id) return;
    if (plan.tier === 0) return;

    const confirmed = await modals.confirm({
      title: 'Cambiar plan',
      message:
        'Importante: el cambio puede generar un prorrateo del ciclo actual ' +
        'que se reflejará en el próximo cobro. ' +
        'El cambio aplica al instante y recibirá una notificación.',
      acceptText: 'Cambiar plan',
      cancelText: 'Cancelar',
      acceptColor: 'warning',
    });

    if (!confirmed) return;

    try {
      purchasingTier.value = plan.tier;
      const summary = await billingService.changeSubscriptionPlan({
        restaurantId: props.restaurant.id,
        tier: plan.tier,
        subTier: plan.subTier ?? null,
        periodMonths: selectedPeriod.value,
      });

      exitChoose();

      subscription.value = summary;

      if (summary.pendingConfirmation) {
        ui.showToast(AlertType.Info, 'Cambio solicitado. Confirmando pago…');
        const confirmedNow = await pollReconcileUntilConfirmed();
        if (confirmedNow) {
          ui.showToast(AlertType.Success, 'Plan actualizado');
        } else {
          ui.showToast(
            AlertType.Info,
            'El cambio fue solicitado, pero el cobro aún no se confirmó. Puede actualizar el estado en unos segundos.'
          );
        }
      } else {
        ui.showToast(AlertType.Success, 'Plan actualizado');
      }

      await Promise.all([refreshRestaurant(), refreshSubscription()]);
      await restaurantStore.loadBillingSubscriptionSummary({ force: true });
    } catch (e) {
      const renewalMessage = getRenewalInProgressMessage(e);
      if (renewalMessage) {
        ui.showToast(AlertType.Info, renewalMessage);
        await reconcileSubscription();
        exitChoose();
      } else {
        ui.serverError(e, 'No se pudo cambiar el plan');
      }
    } finally {
      purchasingTier.value = null;
    }
  }

  async function onSelectPlan(plan: IBillingPlanCatalogItem) {
    if (choosePurpose.value === 'change') {
      await changePlan(plan);
      return;
    }

    if (isLegacyActiveWithoutSubscription.value) {
      const currentTier = purchasedTier.value;
      const currentSubTier = props.restaurant?.subTier ?? 0;
      const selectedSubTier = plan.subTier ?? 0;

      if (plan.tier !== currentTier || selectedSubTier !== currentSubTier) {
        ui.showToast(
          AlertType.Info,
          'Puede cambiar de plan después de renovar su plan actual.'
        );
        return;
      }
    }

    await startCheckout(plan);
  }

  async function openStripeCustomerPortal() {
    if (!props.restaurant?.id) return;

    try {
      loading.value = true;
      const res = await billingService.createCustomerPortalSession(
        props.restaurant.id
      );
      if (!res?.url) {
        ui.showToast(
          AlertType.Error,
          'No se pudo abrir el portal de facturación'
        );
        return;
      }

      window.location.href = res.url;
    } catch (e) {
      ui.serverError(e, 'No se pudo abrir el portal de facturación');
    } finally {
      loading.value = false;
    }
  }
</script>

<template>
  <div class="restaurant-billing-plans">
    <v-overlay :value="loading || purchasingTier !== null">
      <v-progress-circular indeterminate size="48" />
    </v-overlay>

    <v-alert
      v-if="billingUnavailable"
      class="restaurant-billing-plans__billing-unavailable"
      type="warning"
      rounded="lg"
      dense
    >
      <div class="restaurant-billing-plans__billing-unavailable-content">
        <div class="restaurant-billing-plans__billing-unavailable-text">
          La facturación por suscripción no está disponible en este momento. La
          renovación por el sistema anterior fue deprecada. Reintente o contacte a
          soporte.
        </div>

        <v-btn
          class="restaurant-billing-plans__billing-unavailable-retry"
          depressed
          small
          :disabled="loading || purchasingTier !== null"
          @click="loadBillingState"
        >
          Reintentar
        </v-btn>
      </div>
    </v-alert>

    <div
      v-if="!billingUnavailable && recoveryAlertText"
      class="restaurant-billing-plans__recovery"
    >
      <v-alert
        class="restaurant-billing-plans__alert"
        type="warning"
        rounded="lg"
        dense
      >
        {{ recoveryAlertText }}
      </v-alert>

      <v-btn
        v-if="showRecoveryUpdatePaymentMethod"
        class="restaurant-billing-plans__portal"
        depressed
        :disabled="loading || purchasingTier !== null"
        @click="openStripeCustomerPortal"
      >
        <v-icon left>mdi-credit-card-outline</v-icon>
        Actualizar medio de pago
      </v-btn>
    </div>

    <BillingSubscriptionManageCard
      v-if="showManage"
      class="restaurant-billing-plans__manage-card"
      :plan-name="`Plan ${subscriptionTierLabel}`"
      :status-text="manageStatus.text"
      :status-color="manageStatus.color"
      :current-access-name="`Plan ${currentTierLabel}`"
      :plan-expires-at="planExpiresAtIso"
      :next-billing-at="subscription ? subscription.nextBillingAt : null"
      :cancel-at-period-end="
        subscription ? subscription.cancelAtPeriodEnd : false
      "
      :pending-confirmation="
        subscription ? subscription.pendingConfirmation : false
      "
      :renewal-pending="renewalPending"
      :primary-action-text="managePrimaryActionText"
      :disable-actions="
        billingUnavailable || loading || purchasingTier !== null
      "
      :show-customer-portal="showManageStripeCustomerPortal"
      :can-cancel="canCancel"
      @change-plan="handlePrimaryAction"
      @cancel="cancelSubscription"
      @refresh="refreshPendingConfirmation"
      @open-portal="openStripeCustomerPortal"
    />

    <div v-if="!showManage">
      <BillingLegacyPlanStatusCard
        v-if="
          showLegacyPlanStatusCard || (billingUnavailable && purchasedTier > 0)
        "
        class="restaurant-billing-plans__legacy-card"
        :plan-name="`Plan ${purchasedTierLabel}`"
        :status-text="legacyStatus.text"
        :status-color="legacyStatus.color"
        :current-access-name="`Plan ${purchasedTierLabel}`"
        :plan-expires-at="planExpiresAtIso"
      />

      <div v-if="!billingUnavailable" class="restaurant-billing-plans__header">
        <div class="restaurant-billing-plans__header-text">
          <div class="restaurant-billing-plans__title">{{ chooseTitle }}</div>
          <div class="restaurant-billing-plans__subtitle">
            <v-icon class="restaurant-billing-plans__subtitle-icon" size="16">
              mdi-lock
            </v-icon>
            <span>{{ chooseSubtitle }}</span>
          </div>
        </div>

        <v-btn
          v-if="hasPaidAccess && hasManageableSubscription"
          class="restaurant-billing-plans__back"
          depressed
          @click="exitChoose"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
      </div>

      <div
        v-if="billingUnavailable"
        class="restaurant-billing-plans__unavailable-state"
      >
        <v-icon class="restaurant-billing-plans__unavailable-icon" size="28">
          mdi-alert-circle-outline
        </v-icon>
        <div class="restaurant-billing-plans__unavailable-text">
          No se pudieron cargar los planes.
        </div>
      </div>

      <v-alert
        v-if="!billingUnavailable && isPlanExpired"
        class="restaurant-billing-plans__alert"
        type="warning"
        rounded="lg"
        dense
      >
        Su plan venció. Renuévelo para seguir usando funciones Pro.
      </v-alert>

      <template v-if="showPlanPicker">
        <BillingPlanPicker
          v-if="catalog"
          :catalog="catalog"
          :selected-period="selectedPeriod"
          :mode="choosePurpose"
          :busy="loading || purchasingTier !== null"
          :current-tier="subscription ? subscription.subscriptionTier : null"
          :current-period-months="
            subscription ? subscription.periodMonths : null
          "
          @period-change="selectedPeriod = $event"
          @select-plan="onSelectPlan"
        />

        <div v-else class="restaurant-billing-plans__loading">
          <v-progress-circular
            indeterminate
            size="28"
            width="3"
            color="orange"
          />
          <div class="restaurant-billing-plans__loading-text">
            Cargando planes…
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .restaurant-billing-plans {
    max-width: 1140px;
    margin: 0 auto;
    padding: 6px 2px;

    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 14px;
      padding: 14px 14px;
      border-radius: 16px;
      border: 1px solid rgba(15, 23, 42, 0.08);
      background: radial-gradient(
          900px 420px at 0% 0%,
          rgba(255, 111, 0, 0.12),
          rgba(255, 255, 255, 0)
        ),
        rgba(255, 255, 255, 0.75);
    }

    &__title {
      font-size: 22px;
      font-weight: 900;
      letter-spacing: -0.3px;
      color: rgba(15, 23, 42, 0.92);
      line-height: 1.2;
      margin-bottom: 6px;
    }

    &__subtitle {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: rgba(15, 23, 42, 0.62);
      font-weight: 700;
    }

    &__subtitle-icon {
      color: rgba(15, 23, 42, 0.48);
    }

    &__back {
      border-radius: 14px;
      height: 44px;
      text-transform: none;
      font-weight: 900;
      letter-spacing: 0.2px;
      background: rgba(15, 23, 42, 0.92) !important;
      color: #fff !important;
      box-shadow: 0 16px 30px rgba(15, 23, 42, 0.18) !important;
    }

    &__alert {
      margin-bottom: 14px;
    }

    &__portal {
      border-radius: 14px;
      height: 44px;
      text-transform: none;
      font-weight: 900;
      letter-spacing: 0.2px;
      background: rgba(15, 23, 42, 0.92) !important;
      color: #fff !important;
      box-shadow: 0 16px 30px rgba(15, 23, 42, 0.18) !important;
      margin-bottom: 14px;
    }

    &__recovery {
      margin-bottom: 14px;
    }

    &__manage-card {
      margin-top: 4px;
    }

    &__legacy-card {
      margin-top: 4px;
      margin-bottom: 14px;
    }

    &__billing-unavailable {
      margin-bottom: 14px;
    }

    &__billing-unavailable-content {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
    }

    &__billing-unavailable-text {
      flex: 1 1 420px;
    }

    &__billing-unavailable-retry {
      border-radius: 12px;
      text-transform: none;
      font-weight: 900;
      letter-spacing: 0.2px;
    }

    &__loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 28px 0;
      color: rgba(15, 23, 42, 0.62);
    }

    &__loading-text {
      font-size: 13px;
      font-weight: 800;
    }

    &__unavailable-state {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 18px 0 22px;
      color: rgba(15, 23, 42, 0.7);
    }

    &__unavailable-icon {
      color: rgba(180, 83, 9, 0.88);
    }

    &__unavailable-text {
      font-size: 13px;
      font-weight: 800;
    }
  }
</style>
